import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, MailMinus, RefreshCw, Check } from 'lucide-react';
import { auth } from '../firebase';
import { apiHeaders } from '../utils/api';
import { usePlan } from '../App';

const API = 'https://aglomera.com/api';
const PUB = process.env.PUBLIC_URL;

// ── Définition des 3 newsletters fixes ────────────────────────────────────────
export const NEWSLETTERS = [
  {
    slug: 'corporate-finance',
    nom: 'Corporate Finance',
    tagline: 'Les offres des métiers de la finance d\'entreprise',
    keywords: [
      'M&A', 'Private Equity', 'Audit', 'ECM/DCM',
      'Transaction Services', 'Leveraged Finance', 'Contrôle de gestion', 'Venture Capital',
    ],
    image: `${PUB}/nl-banners/image-corporatefinance.webp`,
    couleur: '#1a3a70',
    couleurLight: '#eef2fc',
    couleurBorder: '#c0cff5',
  },
  {
    slug: 'financial-markets',
    nom: 'Market Finance',
    tagline: 'Les offres des métiers des marchés financiers',
    keywords: [
      'Sales', 'Trading', 'Structuring', 'Asset Management', 'Quant', 'Risk Manager',
    ],
    image: `${PUB}/nl-banners/image-marketfinance.webp`,
    couleur: '#0f4c81',
    couleurLight: '#e8f0fb',
    couleurBorder: '#a0bde8',
  },
  {
    slug: 'gestion-de-patrimoine',
    nom: 'Gestion de patrimoine',
    tagline: 'Les offres des métiers de la gestion de patrimoine',
    keywords: ['CGP', 'Private banking', 'Family office', 'Ingénieur patrimonial', 'Gestion de fortune'],
    image: `${PUB}/nl-banners/image-patrimoine.webp`,
    couleur: '#2c5f6f',
    couleurLight: '#e7f0f2',
    couleurBorder: '#a8cdd6',
  },
];

type Frequence = 'deux_par_jour' | 'quotidienne' | 'mardi_vendredi';

const FREQUENCES: { key: Frequence; label: string; sub: string }[] = [
  { key: 'deux_par_jour',  label: '2× par jour',      sub: 'Midi & soir' },
  { key: 'quotidienne',    label: 'Quotidienne',       sub: 'Chaque soir' },
  { key: 'mardi_vendredi', label: '2× par semaine',    sub: 'Mar · Ven' },
];

interface Abonnement {
  id: number;
  newsletter_slug: string;
  localisation: string;
  types_contrat: string[];
}

const MesNewsletters: React.FC = () => {
  const navigate = useNavigate();
  const { plan } = usePlan();
  const [abonnements, setAbonnements] = useState<Abonnement[]>([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState<string | null>(null);
  const [unsubscribing, setUnsubscribing] = useState<string | null>(null);
  const [popupSlug, setPopupSlug] = useState<string | null>(null);

  const maxNewsletters = plan === 'premium' ? 3 : 1;
  const [freqSaved, setFreqSaved] = useState<Frequence>('mardi_vendredi');
  const [freqPending, setFreqPending] = useState<Frequence>('mardi_vendredi');
  const [freqSavedOk, setFreqSavedOk] = useState(false);
  const [notifActives, setNotifActives] = useState(true);
  const [notifSaving, setNotifSaving] = useState(false);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    (async () => {
      fetch(`${API}/newsletters/abonnements/`, {
        headers: await apiHeaders(),
      })
        .then(r => r.ok ? r.json() : [])
        .then(data => setAbonnements(Array.isArray(data) ? data : []))
        .catch(() => setAbonnements([]))
        .finally(() => setLoading(false));
    })();
  }, []);

  // Charger la fréquence depuis l'API
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    (async () => {
      fetch(`${API}/profil/`, { headers: await apiHeaders() })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data?.frequence_email) {
            const f = data.frequence_email as Frequence;
            setFreqSaved(f);
            setFreqPending(f);
          } else {
            const initial: Frequence = 'mardi_vendredi';
            setFreqSaved(initial);
            setFreqPending(initial);
          }
          if (data?.notifications_email_actives !== undefined) {
            setNotifActives(!!data.notifications_email_actives);
          }
        })
        .catch(() => {});
    })();
  }, [plan]);

  const slugsAbonnes = abonnements.map(a => a.newsletter_slug);
  const estAbonne = (slug: string) => slugsAbonnes.includes(slug);
  const quotaAtteint = slugsAbonnes.length >= maxNewsletters;

  const handleSabonner = async (slug: string) => {
    const user = auth.currentUser;
    if (!user) return;
    setSubscribing(slug);
    try {
      const res = await fetch(`${API}/newsletters/abonnements/`, {
        method: 'POST',
        headers: await apiHeaders(),
        body: JSON.stringify({ newsletter_slug: slug, localisation: '', types_contrat: [] }),
      });
      if (res.ok) {
        const data = await res.json();
        setAbonnements(prev => [...prev.filter(a => a.newsletter_slug !== slug), data]);
        // Popup de confirmation — pas de redirection
        setPopupSlug(slug);
      }
    } finally {
      setSubscribing(null);
    }
  };

  const handleDesabonner = async (slug: string) => {
    const user = auth.currentUser;
    if (!user) return;
    const ab = abonnements.find(a => a.newsletter_slug === slug);
    if (!ab) return;
    setUnsubscribing(slug);
    try {
      await fetch(`${API}/newsletters/abonnements/${ab.id}/`, {
        method: 'DELETE',
        headers: await apiHeaders(),
      });
      setAbonnements(prev => prev.filter(a => a.newsletter_slug !== slug));
    } finally {
      setUnsubscribing(null);
    }
  };

  const handleToggleNotif = async (val: boolean) => {
    setNotifActives(val);
    setNotifSaving(true);
    try {
      await fetch(`${API}/profil/`, {
        method: 'PATCH',
        headers: await apiHeaders(),
        body: JSON.stringify({ notifications_email_actives: val }),
      });
    } catch { /* ignore */ } finally {
      setNotifSaving(false);
    }
  };

  const handleSaveFrequence = async () => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      await fetch(`${API}/profil/`, {
        method: 'PATCH',
        headers: await apiHeaders(),
        body: JSON.stringify({ frequence_email: freqPending }),
      });
    } catch { /* ignore */ }
    setFreqSaved(freqPending);
    setFreqSavedOk(true);
    setTimeout(() => setFreqSavedOk(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 rounded-full border-2 animate-spin"
          style={{ borderColor: '#2a52a8', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  const popupNl = NEWSLETTERS.find(n => n.slug === popupSlug);

  return (
    <div className="p-8 w-full">

      {/* ── Popup confirmation abonnement ──────────────────────────────────── */}
      {popupSlug && popupNl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(15,30,60,0.45)', backdropFilter: 'blur(4px)' }}
          onClick={() => setPopupSlug(null)}
        >
          <div
            className="bg-white rounded-3xl shadow-2xl max-w-sm w-full overflow-hidden"
            style={{ border: `2px solid ${popupNl.couleurBorder}` }}
            onClick={e => e.stopPropagation()}
          >
            {/* Bande colorée en haut */}
            <div className="h-2 w-full" style={{ background: popupNl.couleur }} />

            <div className="px-8 py-7 flex flex-col items-center text-center gap-4">
              {/* Icône checkmark */}
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{ background: popupNl.couleurLight }}
              >
                <CheckCircle size={34} style={{ color: popupNl.couleur }} strokeWidth={1.8} />
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900">Abonnement activé !</h3>
                <p className="text-sm text-gray-500 mt-2 leading-relaxed">
                  Vous êtes maintenant abonné à la newsletter{' '}
                  <span className="font-semibold" style={{ color: popupNl.couleur }}>
                    {popupNl.nom}
                  </span>.
                  <br />Vos offres sont disponibles dans <strong>Mes alertes</strong>.
                </p>
              </div>

              <div className="flex gap-3 w-full pt-1">
                <button
                  onClick={() => { setPopupSlug(null); navigate('/alertes'); }}
                  className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
                  style={{ background: popupNl.couleur }}
                >
                  Voir mes alertes →
                </button>
                <button
                  onClick={() => setPopupSlug(null)}
                  className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-500 border border-gray-200 hover:bg-gray-50 transition-all"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── En-tête ─────────────────────────────────────────────────────────── */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Choix newsletter</h1>
        <p className="text-sm text-gray-500 mt-1">
          Abonnez-vous à une newsletter thématique et recevez les meilleures offres.
        </p>
      </div>

      {/* ── Abonnements actifs (avec animation) ─────────────────────────────── */}
      {slugsAbonnes.length > 0 && (
        <div className="mb-8">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">
            Mes abonnements actifs
          </p>
          <div className="flex flex-wrap gap-3">
            {NEWSLETTERS.filter(n => estAbonne(n.slug)).map(n => (
              <button
                key={n.slug}
                onClick={() => navigate('/alertes')}
                className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 shadow-sm"
                style={{ background: n.couleur }}
              >
                {/* Point animé */}
                <span className="relative flex h-2.5 w-2.5">
                  <span
                    className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
                    style={{ background: 'rgba(255,255,255,0.7)' }}
                  />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white" />
                </span>
                {n.nom}
                <span className="text-xs font-normal opacity-80">Voir les offres →</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Grille newsletters ───────────────────────────────────────────────── */}
      <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">
        {slugsAbonnes.length > 0 ? 'Newsletters disponibles' : 'Choisissez votre newsletter'}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5" style={{ gridAutoRows: '1fr' }}>
        {NEWSLETTERS.map(n => {
          const isSoon = n.slug === 'gestion-de-patrimoine';
          const abonne = estAbonne(n.slug);
          const bloque = !abonne && quotaAtteint;
          const isSubLoading = subscribing === n.slug;
          const isUnsubLoading = unsubscribing === n.slug;

          return (
            <div
              key={n.slug}
              className="rounded-2xl overflow-hidden border flex flex-col transition-all h-full relative"
              style={{
                borderColor: abonne ? n.couleur : '#e5e7eb',
                opacity: isSoon ? 0.65 : bloque ? 0.55 : 1,
              }}
            >
              {/* Header image */}
              <div className="relative overflow-hidden" style={{ minHeight: 130 }}>
                <img src={n.image} alt={n.nom} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0" style={{ background: `${n.couleur}cc` }} />
                {isSoon && (
                  <div style={{ position: 'absolute', top: 0, right: 0, width: 88, height: 88, overflow: 'hidden', pointerEvents: 'none', zIndex: 10 }}>
                    <div style={{ position: 'absolute', top: 18, right: -38, width: 144, background: n.couleur, color: 'white', fontSize: 9, fontWeight: 700, textAlign: 'center', padding: '4px 0', transform: 'rotate(45deg)', letterSpacing: '0.05em', boxShadow: '0 2px 6px rgba(0,0,0,0.12)' }}>
                      Bientôt
                    </div>
                  </div>
                )}
                <div className="relative z-10 px-6 pt-5 pb-5">
                  <p className="text-xs font-bold uppercase tracking-widest text-white opacity-70 mb-1">
                    Newsletter
                  </p>
                  <h2 className="text-xl font-bold text-white drop-shadow">{n.nom}</h2>
                  <p className="text-sm text-white opacity-90 mt-1 drop-shadow">{n.tagline}</p>
                </div>
              </div>

              {/* Corps */}
              <div className="bg-white px-6 py-5 flex flex-col flex-1">
                {/* Keywords */}
                <div className="flex flex-wrap gap-1.5 justify-center flex-1 content-start mb-5">
                  {n.keywords.map(kw => (
                    <span key={kw} className="text-xs font-medium px-2.5 py-1 rounded-full"
                      style={{ background: n.couleurLight, color: n.couleur }}>
                      {kw}
                    </span>
                  ))}
                </div>

                {/* Bouton bas */}
                {abonne ? (
                  <div className="flex flex-col gap-2">
                    {/* Badge abonné */}
                    <div className="flex items-center gap-2 px-3 py-2 rounded-xl"
                      style={{ background: n.couleurLight }}>
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60"
                          style={{ background: n.couleur }} />
                        <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: n.couleur }} />
                      </span>
                      <span className="text-xs font-semibold" style={{ color: n.couleur }}>
                        Abonné · offres disponibles dans Mes alertes
                      </span>
                    </div>
                    {/* Bouton désabonnement */}
                    <button
                      onClick={() => handleDesabonner(n.slug)}
                      disabled={isUnsubLoading}
                      className="flex items-center justify-center gap-2 w-full py-2 rounded-xl text-xs font-medium border border-gray-200 text-gray-400 hover:border-red-200 hover:text-red-500 hover:bg-red-50 transition-all disabled:opacity-50"
                    >
                      {isUnsubLoading
                        ? <><RefreshCw size={12} className="animate-spin" /> Désabonnement...</>
                        : <><MailMinus size={13} /> Se désabonner</>
                      }
                    </button>
                  </div>
                ) : bloque ? (
                  <div className="text-center">
                    <p className="text-xs text-gray-500 mb-2">Limite atteinte</p>
                    {plan !== 'premium' && (
                      <button
                        onClick={() => navigate('/abonnement')}
                        className="w-full py-2.5 rounded-xl text-sm font-semibold transition-all border"
                        style={{ borderColor: n.couleur, color: n.couleur }}
                      >
                        {plan === 'pro' ? 'Passer au plan Premium' : 'Passer au plan Pro ou Premium'}
                      </button>
                    )}
                  </div>
                ) : isSoon ? (
                  <button
                    disabled
                    className="w-full py-2.5 rounded-xl text-sm font-semibold transition-all cursor-not-allowed"
                    style={{ background: '#e5e7eb', color: '#9ca3af' }}
                  >
                    Bientôt disponible
                  </button>
                ) : (
                  <button
                    onClick={() => handleSabonner(n.slug)}
                    disabled={isSubLoading}
                    className="w-full py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-60"
                    style={{ background: n.couleur }}
                  >
                    {isSubLoading ? 'Abonnement...' : "S'abonner"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Bannière plan ───────────────────────────────────────────────────── */}
      <div className="mt-8 p-5 rounded-2xl border border-gray-100 bg-white flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-gray-700">
            Plan {plan.charAt(0).toUpperCase() + plan.slice(1)} —{' '}
            {slugsAbonnes.length}/{maxNewsletters} newsletter{maxNewsletters > 1 ? 's' : ''} active{maxNewsletters > 1 ? 's' : ''}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">
            {plan === 'gratuit' && ''}
            {plan === 'pro' && 'Toutes les offres disponibles · 1 newsletter'}
            {plan === 'premium' && 'Toutes les offres · 3 newsletters simultanées'}
          </p>
        </div>
        {plan === 'gratuit' && (
          <button onClick={() => navigate('/abonnement')}
            className="text-xs font-semibold px-4 py-2 rounded-xl text-white flex-shrink-0"
            style={{ background: '#2a52a8' }}>
            Passer au plan Pro ou Premium
          </button>
        )}
        {plan === 'pro' && (
          <button onClick={() => navigate('/abonnement')}
            className="text-xs font-semibold px-4 py-2 rounded-xl text-white flex-shrink-0"
            style={{ background: '#2a52a8' }}>
            Passer au plan Premium
          </button>
        )}
      </div>

      {/* ── Fréquence des emails ─────────────────────────────────────────────── */}
      <div className="mt-6 p-6 rounded-2xl border border-gray-100 bg-white">
        <div className="mb-4">
          <h2 className="text-sm font-bold text-gray-800">Fréquence des emails</h2>
          <p className="text-xs text-gray-400 mt-0.5">Choisissez quand recevoir vos alertes par email</p>
        </div>

        {plan === 'gratuit' && (
          <div className="mb-3">
            <button
              onClick={() => navigate('/abonnement')}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg text-white"
              style={{ background: '#2a52a8' }}
            >
              Passer au plan Pro →
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {FREQUENCES.map(f => {
            const isSoon = f.key === 'deux_par_jour';
            const locked = isSoon;
            const active = freqPending === f.key && !isSoon;
            return (
              <button
                key={f.key}
                onClick={() => !locked && setFreqPending(f.key)}
                disabled={locked}
                className="relative flex flex-col items-center gap-1.5 px-4 py-4 rounded-xl border-2 transition-all text-center overflow-hidden"
                style={isSoon
                  ? { borderColor: '#e5e7eb', background: '#f9fafb', opacity: 0.65, cursor: 'not-allowed' }
                  : locked
                    ? { borderColor: '#e5e7eb', background: '#f9fafb', opacity: 0.45, cursor: 'not-allowed' }
                    : active
                      ? { borderColor: '#2a52a8', background: '#eef2fc' }
                      : { borderColor: '#e5e7eb', background: '#fafafa' }
                }
              >
                {/* Ruban "Bientôt" en coin supérieur droit */}
                {isSoon && (
                  <div style={{
                    position: 'absolute', top: 0, right: 0,
                    width: 88, height: 88, overflow: 'hidden', pointerEvents: 'none',
                  }}>
                    <div style={{
                      position: 'absolute', top: 18, right: -38,
                      width: 144, background: '#2a52a8', color: 'white',
                      fontSize: 9, fontWeight: 700, textAlign: 'center',
                      padding: '4px 0', transform: 'rotate(45deg)',
                      letterSpacing: '0.05em', boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                    }}>
                      Bientôt
                    </div>
                  </div>
                )}
                <span className="text-sm font-bold" style={{ color: locked ? '#9ca3af' : active ? '#2a52a8' : '#374151' }}>
                  {f.label}
                </span>
                <span className="text-xs" style={{ color: locked ? '#d1d5db' : active ? '#2a52a8' : '#9ca3af' }}>
                  {f.sub}
                </span>
                <span
                  className="mt-1 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all"
                  style={active
                    ? { borderColor: '#2a52a8', background: '#2a52a8' }
                    : { borderColor: '#d1d5db', background: 'white' }
                  }
                >
                  {active && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                </span>
              </button>
            );
          })}
        </div>
        <div className="mt-4 flex items-center gap-3">
          <button
            onClick={handleSaveFrequence}
            disabled={freqPending === freqSaved}
            className="px-5 py-2 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-40"
            style={{ background: '#2a52a8' }}
          >
            Sauvegarder
          </button>
          {freqSavedOk && (
            <span className="flex items-center gap-1.5 text-sm font-medium" style={{ color: '#2a52a8' }}>
              <Check size={15} strokeWidth={2.5} />
              Sauvegardé
            </span>
          )}
        </div>

        {/* Note spam */}
        <p className="mt-3 text-xs text-gray-400">
          💡 Si vous ne recevez pas nos emails, pensez à vérifier vos spams.
        </p>

        {/* Opt-out notifications */}
        <div id="bas" className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-700">Notifications par email</p>
            <p className="text-xs text-gray-400 mt-0.5">Désactiver pour ne plus recevoir aucun email de notre part</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer" style={{ opacity: notifSaving ? 0.6 : 1 }}>
            <input
              type="checkbox"
              className="sr-only peer"
              checked={notifActives}
              onChange={e => handleToggleNotif(e.target.checked)}
              disabled={notifSaving}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" style={{ '--tw-bg-opacity': '1' } as React.CSSProperties}></div>
          </label>
        </div>
      </div>

    </div>
  );
};

export default MesNewsletters;
